/**
 * Authenticator-app (TOTP) enrollment and verification through Supabase Auth.
 * AuthContext wraps these so a successful verification also publishes the
 * upgraded (aal2) session.
 */

import { totpQrCodeSrc } from './mfa';
import { supabase } from './supabaseClient';
import type { TotpEnrollment } from './types';

const TOTP_FRIENDLY_NAME = 'Authenticator app';

/**
 * Begin setting up an authenticator app. An abandoned earlier setup leaves an
 * unverified factor behind, and Supabase rejects a second factor with the
 * same friendly name, so those are removed first.
 *
 * The factor list here comes from the server; if it already shows a verified
 * authenticator (the session's cached copy was stale), setup stops with a
 * clear message — Supabase would refuse a new factor from this aal1 session.
 */
export async function startTotpEnrollment(): Promise<TotpEnrollment> {
  const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
  if (listError) throw listError;

  if (factors.totp.length > 0) {
    throw new Error(
      'This account already has an authenticator app. Sign out, then sign in again to enter its code.'
    );
  }

  const abandoned = factors.all.filter(
    (factor) => factor.factor_type === 'totp' && factor.status === 'unverified'
  );
  const removals = await Promise.all(
    abandoned.map((factor) => supabase.auth.mfa.unenroll({ factorId: factor.id }))
  );
  const removalError = removals.find((result) => result.error)?.error;
  if (removalError) throw removalError;

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: TOTP_FRIENDLY_NAME,
  });
  if (error) throw error;

  return {
    factorId: data.id,
    qrCodeSrc: totpQrCodeSrc(data.totp.qr_code),
    secret: data.totp.secret,
  };
}

/** Verify a 6-digit code for a factor. On success Supabase stores an aal2 session. */
export async function verifyTotpCode(factorId: string, code: string): Promise<void> {
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
  if (error) throw error;
}
